import * as React from 'react';
import { cn } from '../../utils';
import { Input } from '../../components/inputs/Input';
import { Icon } from '../../components/icons/Icon';
import { useTranslation } from '../../providers/UIProvider';

// ---------------------------------------------------------------------------
// Re-exported for backward compatibility (used by SortButton)
// ---------------------------------------------------------------------------

export interface SidebarSortBy {
  /** Sort field key — consumers map their own domain fields onto this */
  field: string;
  order: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Sub-component interfaces
// ---------------------------------------------------------------------------

export interface FiltersSectionProps {
  className?: string;
  children?: React.ReactNode;
}

export interface FiltersTitleProps {
  className?: string;
  children?: React.ReactNode;
}

export interface FiltersContentProps {
  className?: string;
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Main component interface
// ---------------------------------------------------------------------------

export interface FiltersProps {
  /**
   * Controls layout direction.
   * - `'vertical'`   → fixed sidebar panel (hidden below xl breakpoint)
   * - `'horizontal'` → inline bar (hidden on xl+ breakpoint)
   */
  orientation: 'vertical' | 'horizontal';

  className?: string;
  /** Custom filter sections rendered below the search input */
  children?: React.ReactNode;

  searchQuery?: string;
  /** Search handler — when omitted, the built-in search input is not rendered. */
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;

  /**
   * Removes fixed positioning so the component can be placed inside a custom
   * container. Only has effect for `orientation="vertical"`.
   */
  embedded?: boolean;

  /**
   * Optional title rendered at the very top of the vertical sidebar panel.
   * Equivalent to placing `<Filters.Section><Filters.Title>…</Filters.Title></Filters.Section>`
   * as the first child, but more convenient.
   * Only has effect for `orientation="vertical"`.
   */
  title?: React.ReactNode;

  testId?: string;
}

// ---------------------------------------------------------------------------
// Sub-components (defined first so FiltersRoot can reference them directly)
// ---------------------------------------------------------------------------

const Section: React.FC<FiltersSectionProps> = ({ children, className }) => (
  <div className={cn('space-y-md', className)}>{children}</div>
);
Section.displayName = 'Filters.Section';

const Title: React.FC<FiltersTitleProps> = ({ children, className }) => (
  <h3 className={cn('text-sm font-semibold text-foreground-base mb-sm', className)}>{children}</h3>
);
Title.displayName = 'Filters.Title';

const Content: React.FC<FiltersContentProps> = ({ children, className }) => (
  <div className={cn('space-y-sm', className)}>{children}</div>
);
Content.displayName = 'Filters.Content';

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const FiltersRoot: React.FC<FiltersProps> = props => {
  const tSidebar = useTranslation('modules.sidebar');
  const tFilterBar = useTranslation('modules.filterBar');

  const {
    orientation,
    className,
    children,
    searchQuery = '',
    onSearchChange,
    searchPlaceholder,
    embedded = false,
    title,
    testId
  } = props;

  const placeholder =
    searchPlaceholder ||
    (orientation === 'vertical' ? tSidebar('searchPlaceholder') : tFilterBar('searchPlaceholder'));

  const searchInput = onSearchChange && (
    <div className='relative'>
      <Icon
        name='magnifying-glass'
        className='absolute left-3 top-1/2 -translate-y-1/2 !w-4 !h-4 text-foreground-muted'
      />
      <Input
        placeholder={placeholder}
        value={searchQuery}
        onChange={e => onSearchChange(e.target.value)}
        className='pl-9 rounded-md'
        height='md'
      />
    </div>
  );

  // -----------------------------------------------------------------------
  // Vertical (sidebar) layout
  // -----------------------------------------------------------------------

  if (orientation === 'vertical') {
    return (
      <aside
        data-testid={testId}
        className={cn(
          embedded
            ? 'h-full w-full overflow-y-auto'
            : 'hidden xl:block fixed left-0 top-22 h-[calc(100vh-4rem)] w-80 overflow-y-auto border-r border-border-base bg-background-base z-40',
          className
        )}
      >
        <div className={cn(embedded ? 'space-y-lg px-xs' : 'p-lg space-y-lg')}>
          {title && (
            <Section>
              <Title>{title}</Title>
            </Section>
          )}
          {searchInput && (
            <Section>
              <Content>{searchInput}</Content>
            </Section>
          )}
          {children}
        </div>
      </aside>
    );
  }

  // -----------------------------------------------------------------------
  // Horizontal (filter bar) layout
  // -----------------------------------------------------------------------

  return (
    <div data-testid={testId} className={cn('xl:hidden', className)}>
      {children
        ? children
        : searchInput && (
            <div className='flex gap-sm items-stretch'>
              <div className='relative flex-1 min-w-0'>{searchInput}</div>
            </div>
          )}
    </div>
  );
};

FiltersRoot.displayName = 'Filters';

// ---------------------------------------------------------------------------
// Compose — Object.assign infers the full intersection type automatically,
// so consumers see Filters.Section / Filters.Title / Filters.Content on the type.
// ---------------------------------------------------------------------------

export const Filters = Object.assign(FiltersRoot, { Section, Title, Content });
